<template>
  <validation-observer ref="form" #default="{ handleSubmit }">
    <v-form @submit.prevent="handleSubmit(onSaveAndExit)">
      <v-container fluid>
        <v-row
          justify="center"
          :no-gutters="$vuetify.breakpoint.smAndDown"
        >
          <v-col cols="12" xl="9" class="text-justify">
            <v-card flat class="transparent">
              <v-card-text class="pt-0">
                <template v-if="!isLinePartMoldCustomerMode">
                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.model')"
                        :items="modelList"
                        item-text="model"
                        item-value="model"
                        v-model="selectedModel"
                        @change="fetchPartByModel"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.variant')"
                        :items="variantList"
                        :disabled="saving || !selectedModel"
                        :loading="fetchingMatrix"
                        item-text="variant"
                        item-value="variant"
                        v-model="selectedVariant"
                        @change="filterCustomer"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-combobox
                        clearable
                        filled
                        :label="$t('assemblyPlanning.customer')"
                        :items="customers"
                        item-text="customername"
                        item-value="customername"
                        :disabled="saving"
                        v-model="selectedCustomer"
                        @change="filterLine"
                      ></v-combobox>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.line')"
                        :items="lineList"
                        item-text="linename"
                        item-value="linename"
                        :disabled="saving || !selectedCustomer"
                        v-model="selectedLine"
                        @change="filterPart"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="8">
                      <v-text-field
                        :label="$t('assemblyPlanning.remark')"
                        outlined
                        :disabled="!plan"
                        v-model="plan.remark"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.line')"
                        :items="lineList"
                        item-text="linename"
                        item-value="linename"
                        :disabled="saving"
                        :loading="fetchingMatrix"
                        v-model="selectedLine"
                        @change="onLineSelection"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.part')"
                        :items="partList"
                        item-text="partname"
                        item-value="partname"
                        return-object
                        :disabled="saving || !selectedLine"
                        v-model="selectedPart"
                        @change="onPartSelection"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.equipment')"
                        :items="equipmentList"
                        item-text="equipmentname"
                        item-value="equipmentname"
                        :disabled="saving || !selectedPart"
                        v-model="selectedEquipment"
                        @change="onEquipmentSelection"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="4" v-if="isShowColor">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.color')"
                        :items="filteredColorList"
                        :disabled="saving || !selectedPart"
                        item-text="colorname"
                        item-value="colorname"
                        v-model="selectedColor"
                        :title="selectedColor || ''"
                      >
                        <template #item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title v-text="item.colorname"></v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-combobox
                        clearable
                        filled
                        :label="$t('assemblyPlanning.customer')"
                        :items="customers"
                        item-text="customer"
                        item-value="customer"
                        :disabled="saving"
                        v-model="selectedCustomer"
                        @change="onCustomerSelection"
                      ></v-combobox>
                    </v-col>
                    <v-col cols="12" :sm="isShowColor ? 4 : 8">
                      <v-text-field
                        :label="$t('assemblyPlanning.remark')"
                        outlined
                        :disabled="!plan"
                        v-model="plan.remark"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <div class="title mb-2">
                  {{ $t('assemblyPlanning.operationalParams') }}
                  <v-btn
                    small
                    outlined
                    class="text-none"
                    :disabled="!selectedMatrix || saving"
                    @click="editParams = !editParams"
                    color="primary"
                  >
                    <template v-if="!editParams">
                      <v-icon left small>mdi-pencil-outline</v-icon>
                      {{ $t('assemblyPlanning.editButton') }}
                    </template>
                    <template v-else>
                      <v-icon left small>mdi-check</v-icon>
                      {{ $t('assemblyPlanning.doneButton') }}
                    </template>
                  </v-btn>
                </div>
                <v-row v-for="macdetails in plan.machinedetails" :key="macdetails.machinename">
                  <v-col cols="12" sm="3">
                    <validation-provider
                      name="machinename"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.machine')"
                        type="name"
                        disabled
                        :error-messages="errors"
                        prepend-inner-icon="mdi-clock-fast"
                        outlined
                        v-model="macdetails.machinename"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <validation-provider
                      name="cycletime"
                      rules="required|min_value:0.000001"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.cycletime')"
                        type="number"
                        step="any"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-clock-fast"
                        outlined
                        :suffix="$t('assemblyPlanning.secs')"
                        v-model.number="macdetails.stdcycletime"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <validation-provider
                      name="delaytime"
                      rules="required|min_value:0"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.delayTime')"
                        type="number"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-cached"
                        outlined
                        :suffix="$t('assemblyPlanning.secs')"
                        v-model="macdetails.delaytime"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="3">
                      <validation-provider
                        name="activecavity"
                        rules="required|min_value:1"
                        #default="{ errors }"
                      >
                        <v-text-field
                          :label="$t('assemblyPlanning.activeCavity')"
                          type="number"
                          :disabled="!editParams || saving"
                          :error-messages="errors"
                          prepend-inner-icon="mdi-clock-fast"
                          outlined
                          v-model="macdetails.activecavity"
                          hide-details="auto"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                </v-row>
                <div class="title mt-4 mb-2">
                  {{ $t('assemblyPlanning.planDetails') }}
                </div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="quantity"
                      :rules="`required|numeric|min_value:1|multiple_of:${plan.activecavity}`"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.quantity')"
                        type="number"
                        :disabled="saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-package-variant-closed"
                        outlined
                        :suffix="$t('assemblyPlanning.pieces')"
                        v-model="plan.plannedquantity"
                        hide-details="auto"
                        @change="onQuantityChange"
                        :hint="`Should be multiple of active cavity(${plan.activecavity})`"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="scheduledstart"
                      rules="required|greater_than_now"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.scheduledStartTime')"
                        type="datetime-local"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-calendar-check-outline"
                        outlined
                        v-model="plan.scheduledstart"
                        hide-details="auto"
                        :disabled="saving"
                        @change="fetchEstimatedEnd"
                      >
                        <template #append-outer>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon
                                v-text="'$info'"
                                v-on="on"
                                v-bind="attrs"
                              ></v-icon>
                            </template>
                            <ul>
                              <li>
                                Scheduled start time is only used in calculating
                                planned vs actual performance.
                              </li>
                              <li>
                                Updating scheduled start time will not affect
                                the execution order of the plan.
                              </li>
                              <li>
                                Plans are picked automatically in FIFO fashion.
                              </li>
                              <li>
                                To update the execution order, please visit
                                <strong>re-order plans section</strong>.
                              </li>
                            </ul>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="6" sm="2">
                    <v-checkbox
                      hide-details
                      :label="$t('assemblyPlanning.markAsStar')"
                      :disabled="saving"
                      v-model="plan.starred"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="6" sm="2">
                    <v-checkbox
                      hide-details
                      :label="$t('assemblyPlanning.markAsTrial')"
                      :disabled="saving"
                      v-model="plan.trial"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <div>
                  {{ $t('assemblyPlanning.estimatedEnd') }}
                  <strong>{{ estimatedEndDisplay }}</strong>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  color="primary"
                  class="text-none"
                  :disabled="saving"
                  @click="exit"
                >
                  {{ $t('assemblyPlanning.exit') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  class="text-none"
                  :loading="saving
                    || !hasRequiredSelection
                    || !plan.plannedquantity"
                  type="submit"
                >
                  {{ $t('assemblyPlanning.saveExit') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </validation-observer>
</template>

<script>
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
// import { sortArray } from '@shopworx/services/util/sort.service';
import { formatDate } from '@shopworx/services/util/date.service';
import planningConfigMixin, {
  buildPlanMachineDetails,
  fetchPartMatrixRecords,
  resolvePartNetweight,
} from '../../mixins/planning-config';

export default {
  name: 'DuplicatePlan',
  mixins: [planningConfigMixin],
  data() {
    return {
      headers: [
        { text: this.$t('assemblyPlanning.part'), value: 'partname' },
        { text: this.$t('assemblyPlanning.activeCavity'), value: 'activecavity' },
        { text: this.$t('assemblyPlanning.quantity'), value: 'plannedquantity' },
      ],
      editParams: false,
      saving: false,
      fetchingMatrix: false,
      variantList: [],
      // customerList: [],
      lineList: [],
      partList: [],
      equipmentList: [],
      customerOptions: [],
      colorList: [],
      partMatrixList: [],
      partMatrixListData: [],
      selectedModel: null,
      selectedVariant: null,
      selectedCustomer: null,
      selectedLine: null,
      selectedEquipment: null,
      selectedPart: null,
      selectedColor: null,
      plan: {},
    };
  },
  computed: {
    ...mapState('assemblyPlanning', [
      'machines',
      'parts',
      'partMatrix',
      'selectedPlan',
      'customerList',
    ]),
    ...mapGetters('assemblyPlanning', ['selectedAsset', 'partMatrixTags']),
    ...mapGetters('helper', ['locale']),
    hasRequiredSelection() {
      if (this.isLinePartMoldCustomerMode) {
        return !!(
          this.selectedLine
          && this.selectedPart
          && this.selectedEquipment
          && (!this.isShowColor || this.selectedColor)
          && this.selectedCustomer
          && this.selectedMatrix
          && this.selectedMatrix.length
        );
      }
      return !!this.selectedPart;
    },
    modelList() {
      return this.parts;
    },
    customers() {
      if (this.isLinePartMoldCustomerMode) {
        return this.customerOptions;
      }
      return this.customerList;
    },
    estimatedEndDisplay() {
      let res = '';
      if (this.plan.scheduledend) {
        res = formatDate(
          new Date(this.plan.scheduledend),
          'PPpp',
          { locale: this.locale },
        );
      }
      return res;
    },
    selectedMatrix() {
      if (this.isLinePartMoldCustomerMode) {
        if (
          this.selectedPart
          && this.selectedLine
          && this.selectedEquipment
        ) {
          const partMatrix = this.partMatrixListData.filter((matrix) => (
            matrix.partname === this.selectedPart.partname
            && matrix.linename === this.selectedLine
            && matrix.equipmentname === this.selectedEquipment
          ));
          const customerMatrix = this.selectedCustomer
            ? partMatrix.filter((matrix) => matrix.customer === this.selectedCustomer)
            : partMatrix;
          const selectedMatrix = customerMatrix.length ? customerMatrix : partMatrix;
          return selectedMatrix.length
            ? selectedMatrix.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            : null;
        }
        return null;
      }
      if (this.selectedPart && this.selectedLine) {
        const partMatrix = this.partMatrixList.filter((matrix) => (
          matrix.partname === this.selectedPart.partname
          && matrix.linename === this.selectedLine
        ));
        return partMatrix.length
          ? partMatrix.sort((a, b) => a.sequence - b.sequence)
          : null;
      }
      return null;
    },
    filteredColorList() {
      if (!this.isShowColor) {
        return [];
      }

      const normalizedColors = (this.colorList || [])
        .map((color) => ({
          ...color,
          colorname: color.colorname || color.color || color.name,
        }))
        .filter((color) => color.colorname);

      if (!this.selectedPart || !this.selectedPart.partname) {
        return normalizedColors;
      }

      const partName = this.selectedPart.partname;
      const partColors = normalizedColors.filter((color) => (
        !color.partname || color.partname === partName
      ));

      return partColors.length ? partColors : normalizedColors;
    },
    shots() {
      if (this.plan.plannedquantity && this.plan.activecavity) {
        return +this.plan.plannedquantity / +this.plan.activecavity;
      }
      return 0;
    },
  },
  async created() {
    this.clear();
    await this.fetchCustomerList();
    if (this.isLinePartMoldCustomerMode) {
      this.fetchingMatrix = true;
      this.partMatrixListData = await fetchPartMatrixRecords(this.$store);
      if (this.isShowColor) {
        this.colorList = await this.fetchColors();
      }
      this.setAvailableLines();
      this.fetchingMatrix = false;
    }
    this.setPlan();
  },
  methods: {
    ...mapMutations('helper', ['setAlert']),
    ...mapActions('assemblyPlanning', [
      'fetchPartMatrix',
      // 'isFamilyMold',
      // 'getFamilyParts',
      'getScheduledEnd',
      'fetchLastPlan',
      'createPlans',
      'fetchModel',
      'fetchColors',
      'fetchCustomerList',
    ]),
    initPlan() {
      this.plan = {
        assetid: 0,
        partname: '',
        netweight: '',
        linename: '',
        activecavity: 1,
        machinedetails: [],
        plannedquantity: 0,
        scheduledstart: formatDate(new Date(Math.ceil(new Date() / 9e5) * 9e5), 'yyyy-MM-dd\'T\'HH:mm'),
        scheduledend: '',
        starred: false,
        trial: false,
        sortindex: 0,
        status: 'notStarted',
        moldname: '',
        toolname: '',
        remark: '',
      };
    },
    getUniqueRecords(records, key) {
      return [...new Map(
        records
          .filter((record) => record && record[key] !== undefined && record[key] !== null && record[key] !== '')
          .map((record) => [record[key], record]),
      ).values()];
    },
    setAvailableLines() {
      this.lineList = this.getUniqueRecords(this.partMatrixListData, 'linename');
    },
    async setPlan() {
      const [plan] = this.selectedPlan;
      if (this.isLinePartMoldCustomerMode) {
        this.selectedLine = plan.linename;
        this.onLineSelection();
        this.selectedPart = this.partList.find((part) => part.partname === plan.partname) || null;
        this.onPartSelection();
        this.selectedEquipment = plan.moldname || plan.toolname || null;
        this.onEquipmentSelection();
        this.selectedCustomer = plan.customer;
        this.selectedColor = plan.color || null;
        await this.setPlanParameters();
        await this.setEditParams(plan);
        return;
      }
      this.selectedModel = this.modelList
        .find((p) => p.model === plan.model)?.model || plan.model;
      this.fetchingMatrix = true;
      this.variantList = await this.fetchModel({ model: this.selectedModel });
      this.selectedVariant = this.variantList
        .find((p) => p.variant === plan.variant)?.variant || plan.variant;
      this.filterCustomer();
      this.selectedCustomer = this.customers
        .find((p) => p.customername === plan.customer)?.customername || plan.customer;
      this.filterLine();
      this.selectedLine = this.variantList
        .find((m) => m.linename === plan.linename)?.linename || plan.linename;
      this.filterPart();
      this.selectedPart = this.variantList
        .find((p) => p.partname === plan.partname) || null;
      this.fetchingMatrix = false;
      await this.setEditParams(plan);
    },
    async setEditParams(plan) {
      this.plan = {
        // eslint-disable-next-line
        assetid: plan.assetid,
        partname: plan.partname,
        netweight: plan.netweight || '',
        model: plan.model,
        variant: plan.variant,
        customer: plan.customer,
        linename: plan.linename,
        moldname: plan.moldname || '',
        toolname: plan.toolname || '',
        activecavity: plan.activecavity,
        machinedetails: plan.machinedetails,
        plannedquantity: plan.plannedquantity,
        starred: plan.starred,
        trial: plan.trial,
        scheduledstart: formatDate(new Date(Math.ceil(new Date() / 9e5) * 9e5), 'yyyy-MM-dd\'T\'HH:mm'),
        scheduledend: '',
        sortindex: 0,
        status: 'notStarted',
        remark: plan.remark,
        color: plan.color || '',
      };
      await this.fetchEstimatedEnd();
    },
    clear() {
      this.initPlan();
      this.editParams = false;
      this.variantList = [];
      // this.customerList = [];
      this.lineList = [];
      this.partList = [];
      this.equipmentList = [];
      this.customerOptions = [];
      this.selectedModel = null;
      this.selectedVariant = null;
      this.selectedCustomer = null;
      this.selectedLine = null;
      this.selectedEquipment = null;
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.isLinePartMoldCustomerMode) {
        this.setAvailableLines();
      }
      this.$nextTick(() => {
        this.$refs.form.reset();
      });
    },
    async fetchPartByModel() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      this.variantList = [];
      // this.customerList = [];
      this.lineList = [];
      this.partList = [];
      this.selectedVariant = null;
      this.selectedCustomer = null;
      this.selectedLine = null;
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.selectedModel) {
        this.fetchingMatrix = true;
        this.variantList = await this.fetchModel({ model: this.selectedModel });
        this.fetchingMatrix = false;
      }
    },
    filterCustomer() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      // this.customerList = [];
      this.lineList = [];
      this.partList = [];
      this.selectedCustomer = null;
      this.selectedLine = null;
      this.selectedPart = null;
      this.selectedColor = null;
      // if (this.selectedVariant) {
      //   this.customerList = this.variantList
      //     .filter((variant) => variant.variant === this.selectedVariant);
      // }
    },
    filterLine() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      this.lineList = [];
      this.partList = [];
      this.selectedLine = null;
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.selectedCustomer) {
        // this.lineList = this.variantList;
        this.lineList = this.variantList
          .filter((variant) => variant.variant === this.selectedVariant);
      }
    },
    filterPart() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      this.partList = [];
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.selectedLine) {
        this.partList = this.lineList
          .filter((line) => line.linename === this.selectedLine);
        this.partMatrixList = this.partList;
        [this.selectedPart] = this.partList;
        this.setPlanParameters();
      }
    },
    onLineSelection() {
      this.initPlan();
      this.partList = [];
      this.equipmentList = [];
      this.customerOptions = [];
      this.selectedPart = null;
      this.selectedEquipment = null;
      this.selectedCustomer = null;
      this.selectedColor = null;
      if (this.selectedLine) {
        this.partList = this.getUniqueRecords(
          this.partMatrixListData.filter((matrix) => matrix.linename === this.selectedLine),
          'partname',
        );
      }
    },
    onPartSelection() {
      this.initPlan();
      this.equipmentList = [];
      this.customerOptions = [];
      this.selectedEquipment = null;
      this.selectedCustomer = null;
      this.selectedColor = null;
      if (this.selectedLine && this.selectedPart) {
        this.equipmentList = this.getUniqueRecords(
          this.partMatrixListData.filter((matrix) => (
            matrix.linename === this.selectedLine
            && matrix.partname === this.selectedPart.partname
          )),
          'equipmentname',
        );
      }
    },
    onEquipmentSelection() {
      this.initPlan();
      this.customerOptions = [];
      this.selectedCustomer = null;
      this.selectedColor = null;
      if (this.selectedLine && this.selectedPart && this.selectedEquipment) {
        this.customerOptions = this.getUniqueRecords(
          this.partMatrixListData.filter((matrix) => (
            matrix.linename === this.selectedLine
            && matrix.partname === this.selectedPart.partname
            && matrix.equipmentname === this.selectedEquipment
          )),
          'customer',
        );
      }
    },
    onCustomerSelection() {
      if (this.selectedCustomer) {
        this.setPlanParameters();
      }
    },
    async setPlanParameters() {
      const preservedPlan = {
        plannedquantity: this.plan.plannedquantity,
        scheduledstart: this.plan.scheduledstart,
        scheduledend: this.plan.scheduledend,
        starred: this.plan.starred,
        trial: this.plan.trial,
        sortindex: this.plan.sortindex,
        status: this.plan.status,
        remark: this.plan.remark,
      };
      this.initPlan();
      Object.assign(this.plan, preservedPlan);
      if (this.isLinePartMoldCustomerMode) {
        if (
          this.selectedPart
          && this.selectedLine
          && this.selectedEquipment
          && this.selectedCustomer
        ) {
          const [firstMatrix] = this.selectedMatrix || [];
          if (!firstMatrix) {
            return;
          }
          this.plan.assetid = firstMatrix.assetid || this.selectedPart.assetid;
          this.plan.partname = firstMatrix.partname;
          this.plan.netweight = resolvePartNetweight({
            parts: this.parts,
            partname: firstMatrix.partname,
            selectedPart: this.selectedPart,
            fallback: this.plan.netweight || '',
          });
          this.plan.linename = firstMatrix.linename || this.selectedLine;
          this.plan.model = firstMatrix.model;
          this.plan.variant = firstMatrix.variant;
          this.plan.customer = this.selectedCustomer
            || firstMatrix.customer
            || this.plan.customer
            || '';
          this.plan.moldname = firstMatrix.moldname || '';
          this.plan.toolname = firstMatrix.toolname || '';
          this.plan.activecavity = 1;
          this.plan.machinedetails = buildPlanMachineDetails({
            selectedMatrix: this.selectedMatrix,
            machines: this.machines,
            lineName: this.plan.linename,
          });
          if (this.isShowColor && !this.selectedColor && this.filteredColorList.length === 1) {
            [this.selectedColor] = this.filteredColorList.map((color) => color.colorname);
          }
          this.plan.color = this.selectedColor || '';
        }
        return;
      }
      if (this.selectedPart && this.selectedLine) {
        this.plan.assetid = this.selectedPart.assetid;
        this.plan.partname = this.selectedPart.partname;
        this.plan.netweight = resolvePartNetweight({
          parts: this.parts,
          partname: this.selectedPart.partname,
          selectedPart: this.selectedPart,
          fallback: this.plan.netweight || '',
        });
        this.plan.linename = this.selectedLine;
        this.plan.model = this.selectedModel;
        this.plan.variant = this.selectedVariant;
        this.plan.customer = this.selectedCustomer
          || this.plan.customer
          || '';
        this.plan.activecavity = 1;
        this.plan.machinedetails = [];
        this.selectedMatrix.forEach((part) => {
          const {
            machinename,
            stdcycletime,
            linename,
            delaytime,
            cavity,
          } = part;
          this.plan.machinedetails.push({
            machinename,
            stdcycletime,
            linename,
            delaytime,
            activecavity: cavity,
          });
        });
      }
    },
    async fetchEstimatedEnd() {
      if (this.plan.plannedquantity && this.plan.scheduledstart) {
        const machineCount = this.plan.machinedetails.length;
        const totalStdCycletime = this.plan.machinedetails.reduce((acc, cur) => {
          acc += (+cur.stdcycletime * 1000);
          cur.activecavity = +cur.activecavity;
          cur.delaytime = +cur.delaytime;
          return acc;
        }, 0);
        let ctAvg = 0;
        if (machineCount !== 0) {
          ctAvg = Math.floor(totalStdCycletime / machineCount);
        }
        const runTime = ((+this.plan.plannedquantity - 1)
          * ctAvg) + totalStdCycletime;
        this.plan.linestdcycletime = (runTime / 1000);
        this.plan.scheduledend = await this.getScheduledEnd({
          start: new Date(this.plan.scheduledstart).getTime(),
          duration: runTime,
        });
      } else {
        this.plan.scheduledend = '';
      }
    },
    // onCavityChange() {
    //   if (this.familyParts && this.familyParts.length) {
    //     this.updateFamilyParams();
    //   }
    // },
    onQuantityChange() {
      this.fetchEstimatedEnd();
      // if (this.familyParts && this.familyParts.length) {
      //   this.updateFamilyParams();
      // }
    },
    // updateFamilyParams() {
    //   this.familyParts = this.familyParts.map((p) => ({
    //     ...p,
    //     plannedquantity: +p.activecavity * this.shots,
    //   }));
    // },
    // updateFamilyCavity({ id, payload }) {
    //   // eslint-disable-next-line
    //   const index = this.familyParts.findIndex((p) => p._id === id);
    //   this.$set(this.familyParts, index, {
    //     ...this.familyParts[index],
    //     plannedquantity: +payload * this.shots,
    //   });
    // },
    async setSortIndex() {
      const lastPlan = await this.fetchLastPlan();
      if (lastPlan) {
        this.plan.sortindex = lastPlan.sortindex + 100;
      } else {
        this.plan.sortindex = 100;
      }
    },
    exit() {
      this.clear();
      this.selectedPart = null;
      this.$router.go(-1);
    },
    async save() {
      await this.setSortIndex();
      await this.fetchEstimatedEnd();
      // const matrixTags = this.partMatrixTags(this.plan.assetid);
      // const partMatrix = matrixTags.reduce((acc, cur) => {
      //   acc[cur.tagName] = this.selectedMatrix[cur.tagName];
      //   return acc;
      // }, {});
      const payload = [{
        // ...partMatrix,
        ...this.plan,
        ...(this.isShowColor ? { color: this.selectedColor } : {}),
        scheduledstart: new Date(this.plan.scheduledstart).getTime(),
        // familyName: 'family',
      }];
      // eslint-disable-next-line
      // const ids = [...new Set(this.selectedFamilyParts.map((p) => p._id))];
      // eslint-disable-next-line
      // this.selectedFamilyParts = this.familyParts.filter((p) => ids.includes(p._id));
      // if (this.selectedFamilyParts && this.selectedFamilyParts.length) {
      //   const familyPayload = this.selectedFamilyParts.map((p) => ({
      //     ...payload[0],
      //     cavity: p.cavity,
      //     activecavity: p.activecavity,
      //     plannedquantity: p.plannedquantity,
      //     partname: p.partname,
      //   }));
      //   payload = [...payload, ...familyPayload];
      // }
      const created = await this.createPlans(payload);
      if (created) {
        this.setAlert({
          show: true,
          type: 'success',
          message: 'PLAN_CREATED',
        });
      } else {
        this.setAlert({
          show: true,
          type: 'error',
          message: 'ERROR_CREATING_PLAN',
        });
      }
      return created;
    },
    async onSaveAndExit() {
      const isValid = await this.$refs.form.validate();
      if (isValid) {
        this.saving = true;
        const created = await this.save();
        if (created) {
          this.exit();
        }
        this.saving = false;
      }
    },
  },
};
</script>
